import React, { useMemo, useRef, useState } from "react";
import s from "./Wiki.module.css";

/** Мелкие утилиты */
const Kbd = ({ children }) => <kbd className={s.kbd}>{children}</kbd>;

const apiBaseUrl = (process.env.REACT_APP_API_URL || process.env.API_URL || "").replace(/\/$/, "");
const API_V3_DOCS_URL = `${apiBaseUrl}/api/v3/docs`;

const CopyBtn = ({ getText, title = "Скопировать" }) => {
    const [ok, setOk] = useState(false);
    return (
        <button
            type="button"
            className={s.copyBtn}
            title={title}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(getText());
                    setOk(true);
                    setTimeout(() => setOk(false), 1200);
                } catch (e) {
                    console.error("Ошибка копирования:", e);
                }
            }}
        >
            {ok ? "✓" : "⧉"}
        </button>
    );
};

const Code = ({ lang = "json", children }) => {
    const pre = useRef(null);
    const getText = () => (pre.current ? pre.current.innerText : "");
    return (
        <div className={s.code}>
            <div className={s.codeBar}>
                <span className={s.codeLang}>{lang}</span>
                <CopyBtn getText={getText} />
            </div>
            <pre ref={pre}>
                <code>{children}</code>
            </pre>
        </div>
    );
};

/** Вспомогательные генераторы cURL */
const buildCurlPOST = (base, path, headers, body) => {
    const lines = [];
    lines.push(`curl -X POST '${base}${path}' \\`);
    headers.forEach((h) => lines.push(`  -H '${h.name}: ${h.value}' \\`));
    lines.push(`  -d '${String(body).replace(/'/g, "'\\''")}'`);
    return lines.join("\n");
};
const buildCurlGET = (base, path, headers) => {
    const lines = [];
    lines.push(`curl -X GET '${base}${path}' \\`);
    headers.forEach((h, i) => {
        const isLast = i === headers.length - 1;
        lines.push(`  -H '${h.name}: ${h.value}'${isLast ? "" : " \\"}`);
    });
    return lines.join("\n");
};

/** Конфиги провайдеров (для мерчанта) */
const PROVIDERS = {
    /* === CRAFT (Paycraft) === */
    craft: {
        key: "craft",
        label: "Craft",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: true,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "h2h",
                label: "H2H (QR-код)",
                path: "/craft/payment",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "client": "+79001234567",
  "product": "Название товара",
  "sum": 1000,
  "h2h": true,
  "externalReference": "order-123",
  "callbackUrl": "https://merchant.example/callback"
}`,
                successManual: `{
  "transactionId": 2317170,
  "trxUuid": "c09d5d68-8065-4770-...",
  "processingId": 1030674,
  "amount": 1000,
  "currency": "RUB",
  "externalRefs": {
    "payload": "https://v1.npay.kg/#00020101...",
    "expire-at": "2025-12-24T09:03:04.409Z"
  },
  "createdAt": "2025-12-24T08:54:04+00:00",
  "expiresAt": "2025-12-24T09:24:04+00:00"
}`,
            },
            {
                key: "widget",
                label: "Widget (редирект)",
                path: "/craft/payment",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "client": "+79001234567",
  "product": "Название товара",
  "sum": 1000,
  "externalReference": "order-123",
  "redirectUrlSuccess": "https://merchant.example/success",
  "redirectUrlError": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback"
}`,
                successManual: `{
  "transactionId": 2316512,
  "trxUuid": "3a3b8b56-5b8a-4a4e-...",
  "processingId": "abc123...",
  "amount": 1000,
  "currency": "RUB",
  "redirectUrl": "https://paycraftpayment.com/checkout/..."
}`,
            },
        ],
        status: {
            path: function (id) {
                return "/craft/status/" + (id || "<transactionId>");
            },
            success: `{
  "transactionId": 2317170,
  "trxUuid": "c09d5d68-8065-4770-...",
  "externalReference": "order-123",
  "amount": 1000,
  "currency": "RUB",
  "pspState": "SUCCESS",
  "synced": true
}`,
        },
        callbackSample: `{
  "transactionId": 2317170,
  "externalReference": "order-123",
  "state": "SUCCESS",
  "amount": 1000,
  "currency": "RUB"
}`,
        errorSamples: `// 400 Bad Request — отсутствуют обязательные поля
{
  "code": "200",
  "message": "client is required"
}

// 400 Bad Request — неверные данные
{
  "code": "200",
  "message": "amount must be > 0"
}

// 403 Forbidden — неверные учётные данные мерчанта
{
  "code": "200",
  "message": "Merchant auth failed."
}

// 500 Internal Server Error — ошибка провайдера
// Craft передаёт ошибку провайдера как серверную
{
  "code": "100",
  "message": "Internal server error"
}`,
    },

    /* === HYDRA — как было === */
    hydra: {
        key: "hydra",
        label: "Hydra",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: true,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "p2p",
                label: "Создать платёж (P2P)",
                path: "/hydra/p2p",
                bodyWithIp: (label) => `{
  "amount": 1000,
  "currency": "RUB",
  "externalReference": "order-123",
  "description": "${label} test",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback",
  "customerIp": "203.0.113.17"
}`,
                bodyWithoutIp: (label) => `{
  "amount": 1000,
  "currency": "RUB",
  "externalReference": "order-123",
  "description": "${label} test",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback"
}`,
                successManual: `{
  "amount": 1000,
  "currency": "RUB",
  "externalRefs": [ /* массив реквизитов для оплаты */ ],
  "createdAt": "2025-10-02T21:28:24+00:00",
  "expiresAt": "2025-10-02T21:43:24+00:00",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "trxUuid": "3a3b8b56-5b8a-4a4e-8b2b-1f6e8d2a1cde",
  "localTransactionId": 2316512,
  "transactionId": 2316512,
  "hydraId": "5849...8bd9"
}`,
                successLink: `{
  "redirectUrl": "https://hydra.example/checkout/abc123",
  "trxUuid": "3a3b8b56-5b8a-4a4e-8b2b-1f6e8d2a1cde",
  "localTransactionId": 2316512,
  "transactionId": 2316512,
  "hydraId": "5849...8bd9",
  "amount": 1000,
  "currency": "RUB",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline"
// либо
// "paymentUrl": "https://pay.e-clear.cc/<uuid>"
}`,
            },
            {
                key: "sbp",
                label: "Создать платёж (СБП)",
                path: "/hydra/sbp",
                bodyWithIp: (label) => `{
  "amount": 1000,
  "currency": "RUB",
  "externalReference": "order-123",
  "description": "${label} test",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback",
  "customerIp": "203.0.113.17"
}`,
                bodyWithoutIp: (label) => `{
  "amount": 1000,
  "currency": "RUB",
  "externalReference": "order-123",
  "description": "${label} test",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "callbackUrl": "https://merchant.example/callback"
}`,
                successManual: `{
  "amount": 1000,
  "currency": "RUB",
  "externalRefs": [ /* массив реквизитов для оплаты */ ],
  "createdAt": "2025-10-02T21:28:24+00:00",
  "expiresAt": "2025-10-02T21:43:24+00:00",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline",
  "trxUuid": "3a3b8b56-5b8a-4a4e-8b2b-1f6e8d2a1cde",
  "localTransactionId": 2316512,
  "transactionId": 2316512,
  "hydraId": "5849...8bd9"
}`,
                successLink: `{
  "redirectUrl": "https://hydra.example/checkout/abc123",
  "trxUuid": "3a3b8b56-5b8a-4a4e-8b2b-1f6e8d2a1cde",
  "localTransactionId": 2316512,
  "transactionId": 2316512,
  "hydraId": "5849...8bd9",
  "amount": 1000,
  "currency": "RUB",
  "successUrl": "https://merchant.example/success",
  "declineUrl": "https://merchant.example/decline"
// либо
// "paymentUrl": "https://pay.e-clear.cc/<uuid>"
}`,
            },
        ],
        status: {
            path: function (id) {
                return "/hydra/status/" + (id || "<transactionId>");
            },
            success: `{
  "transactionId": 2316512,
  "externalReference": "order-123",
  "amount": 1000,
  "currency": "RUB",
  "processingId": "5849...8bd9",
  "hydraStatus": "COMPLETED",
  "hydraState": "COMPLETED",
  "synced": true,
  "createdAt": "2025-10-02T21:28:24+00:00"
}`,
        },
        errorSamples: `// 400 Bad Request — отсутствуют обязательные поля
{
  "code": "200",
  "message": "amount is required and must be > 0"
}

// 400 Bad Request — ошибка от провайдера Hydracode
{
  "code": "200",
  "message": "Hydracode request failed: Deposit declined"
}

// 403 Forbidden — неверные учётные данные мерчанта
{
  "code": "200",
  "message": "Merchant auth failed."
}

// 400 Bad Request — транзакция не найдена
{
  "code": "200",
  "message": "Transaction not found."
}

// 500 Internal Server Error
{
  "code": "100",
  "message": "Internal server error"
}`,
        callbackSample: `{
  "transactionId": 2316512,
  "externalReference": "order-123",
  "state": "COMPLETED",
  "amount": 1000,
  "currency": "RUB",
  "processingId": "5849...8bd9",
  "createdAt": "2025-10-02T21:28:24+00:00"
}`,
    },

    /** === TKB — единый эндпоинт /api/v1 и выбор операции через method.type === */
    tkb: {
        key: "tkb",
        label: "TKB (выплаты)",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: false,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "payout",
                label: "Выплата (card → TKB)",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "method": { "type": "payout", "provider": "tkb" },
  "amount": { "value": 1000, "currency": "RUB" },
  "card": { "number": "4762085995973998" },
  "recipient": {
    "phone": "+79991112233",
    "email": "user@example.com"
  },
  "description": "Выплата вознаграждения",
  "externalReference": "po-200700",
  "callbackUrl": "https://merchant.example/callback"
}`;
                },
                successManual: `{
  "data": {
    "transactionId": 123456,
    "transactionType": "PAYOUT_CREATED",
    "date": "2025-10-02 21:28:24+00:00",
    "accountingDate": null,
    "amount": 1000,
    "currency": "RUB",
    "authCode": null,
    "pan": "476208******3998"
  },
  "additionalData": {
    "externalReference": "po-200700",
    "description": "Выплата вознаграждения"
  }
}`,
                successLink: null,
            },
            {
                key: "balance",
                label: "Баланс мерчанта",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "method": { "type": "balance" }
}`;
                },
                successManual: `{
  "balance": [
    { "currency": "RUB", "value": 12345.67 }
  ],
  "providers": {
    "a1b2c3d4-e5f6-..." : [
      { "currency": "RUB", "value": 12345.67 }
    ]
  },
  "balances": {
    "items": [
      {
        "terminal_id": 123,
        "terminal_name": "Terminal SBP",
        "merchant_settings_id": 456,
        "transactionBalance": 1234567,
        "transactionBalanceFloat": 12345.67,
        "settlementBalance": 500000,
        "settlementBalanceFloat": 5000.00,
        "actualBalance": 734567,
        "actualBalanceFloat": 7345.67
      }
    ],
    "totals": {
      "transactionBalance": 1234567,
      "transactionBalanceFloat": 12345.67,
      "settlementBalance": 500000,
      "settlementBalanceFloat": 5000.00,
      "actualBalance": 734567,
      "actualBalanceFloat": 7345.67
    }
  }
}`,
                successLink: null,
            },
            {
                key: "status",
                label: "Статус операции",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "method": { "type": "status" },
  "transactionId": 123456
  // либо вместо transactionId:
  // "externalReference": "po-200700"
}`;
                },
                successManual: `[
  {
    "data": {
      "transactionId": 123456,
      "transactionType": "PAYOUT",
      "date": "2025-10-02 21:28:24+00:00",
      "accountingDate": "2025-10-02 21:42:07+00:00",
      "amount": 1000,
      "currency": "RUB",
      "authCode": "123456",
      "pan": "476208******3998"
    },
    "additionalData": {
      "externalReference": "po-200700",
      "description": "Выплата вознаграждения"
    }
  }
]`,
                successLink: null,
            },
        ],
        callbackSample: `{
  "data": {
    "transactionId": 123456,
    "transactionType": "PAYOUT",
    "date": "2025-10-02 21:28:24+00:00",
    "accountingDate": "2025-10-02 21:42:07+00:00",
    "amount": 1000,
    "currency": "RUB",
    "authCode": "123456",
    "pan": "476208******3998"
  },
  "additionalData": {
    "externalReference": "po-200700",
    "description": "Выплата вознаграждения"
  }
}`,
        errorSamples: `// 400 Bad Request — ошибка валидации полей
// Возвращает объект fields с указанием проблемных полей
{
  "code": "200",
  "message": "Validation failed",
  "fields": {
    "payload.amount.value": {
      "code": "300",
      "message": "This value should not be blank."
    },
    "payload.card.pan": {
      "code": "300",
      "message": "Invalid card number format."
    }
  }
}

// 400 Bad Request — дубликат externalReference
{
  "code": "200",
  "message": "Reference po-200700 is not unique."
}

// 400 Bad Request — нет активных настроек мерчанта
{
  "code": "200",
  "message": "No active merchant settings available."
}

// 404 Not Found — транзакция не найдена (проверка статуса)
{
  "code": "200",
  "message": "Not found."
}

// 401 Unauthorized — неверный UUID или ключ мерчанта
{
  "message": "Invalid merchant credentials"
}

// 500 Internal Server Error
{
  "code": "100",
  "message": "Internal server error"
}`,
    },

    /** === ФИНС — выплаты с обязательными паспортными реквизитами === */
    fins: {
        key: "fins",
        label: "ФИНС (выплаты)",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: true,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
            // X-Idempotency-Key мерчанту не нужен — адаптер обеспечивает идемпотентность
        ],
        methods: [
            {
                key: "payout_card",
                label: "Создать выплату (карта)",
                path: "/fins/payouts/card",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "amount": 3000,
  "currency": "RUB",
  "card": { "number": "4762085995973998" },
  "recipient": {
    "last_name": "Петров",
    "first_name": "Петр",
    "middle_name": "Петрович",
    "document_number": "RNxZ 6791", // допустим любое буквенное и числовое значение документа подтверждающего выплату ( номер ПСА )
    "document_date": "2025-05-15",  // допустим также DD.MM.YYYY ( дата ПСА )
    "phone": "+79001234567",
    "email": "example@test.com",
    "inn": "123456789012"
  },
  "external_reference": "po-200700",
  "description": "Выплата вознаграждения",
  "callback_url": "https://merchant.example/callback"

// Если отчества НЕТ, вместо middle_name укажите:
// "recipient": {
//   "last_name": "Петров",
//   "first_name": "Петр",
//   "has_middle_name": false,         // ОБЯЗАТЕЛЬНО, если отчества нет
//   "document_number": "RNxZ 6791",   // допустим любое буквенное и числовое значение документа подтверждающего выплату ( номер ПСА )
//   "document_date": "15.05.2025"     // допустим также DD.MM.YYYY ( дата ПСА )
// }
}`;
                },
                successManual: `{
  "ok": true,
  "data": {
    "created_at": "2025-12-17 12:47:16.036595 +0000 UTC",
    "description": "Общая",
    "id": "d2a099aa-14c8-4f80-8cee-dda895a70635",
    "last_operation_completed_at": "2025-12-17 12:47:18.40332 +0000 UTC",
    "last_operation_status": "approve",
    "last_operation_type": "credit",
    "operations": [
      {
        "amount": {
          "currency": "RUB",
          "value": 3000
        },
        "completed_at": "2025-12-17 12:47:18.40332 +0000 UTC",
        "created_at": "2025-12-17 12:47:16.036595 +0000 UTC",
        "external_transaction_id": "167590",
        "id": "1277b0b6-3710-4215-991d-3d18d8fc7832",
        "payout_method": {
          "card": {
            "number": "220015|1371"
          }
        },
        "rrn": "535146065319",
        "status": "approve",
        "type": "credit"
      }
    ],
    "request_id": "18acdb1f-c9be-4e55-82cf-271272faf148",
    "shop_order_id": "2317149"
  },
  "request_id": "035869c8bc882ce2-a92adcb3f8cf2977"
}`,
            },
            {
                key: "payout_sbp",
                label: "Создать выплату (СБП)",
                path: "/fins/payouts/sbp",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "amount": 3000,
  "currency": "RUB",
  "sbp": {
    "bank_member_id": "100000000011",
    "phone": "+79001234567"          // для СБП обязательно в этом блоке
  },
  "recipient": {
    "last_name": "Петров",
    "first_name": "Петр",
    "middle_name": "Петрович",
    "document_number": "RNxZ 6791", // допустим любое буквенное и числовое значение документа подтверждающего выплату ( номер ПСА )
    "document_date": "2025-05-15",  // допустим также DD.MM.YYYY ( дата ПСА )
    "email": "example@test.com",
    "inn": "123456789012"
    // phone можно также продублировать в recipient (необязательно)
  },
  "external_reference": "po-200700",
  "description": "Выплата вознаграждения",
  "callback_url": "https://merchant.example/callback"

// Если отчества НЕТ, вместо middle_name укажите:
// "recipient": {
//   "last_name": "Петров",
//   "first_name": "Петр",
//   "has_middle_name": false,         // ОБЯЗАТЕЛЬНО, если отчества нет
//   "document_number": "RNxZ 6791",   // допустим любое буквенное и числовое значение документа подтверждающего выплату ( номер ПСА )
//   "document_date": "15.05.2025"     // допустим также DD.MM.YYYY ( дата ПСА )
// }
}`;
                },
                successManual: `{
  "ok": true,
  "data": {
    "created_at": "2025-12-17 12:47:16.036595 +0000 UTC",
    "description": "Общая",
    "id": "c4a9d1d2-aaaa-bbbb-cccc-efefefefefef",
    "last_operation_completed_at": "2025-12-17 12:47:18.40332 +0000 UTC",
    "last_operation_status": "approve",
    "last_operation_type": "credit",
    "operations": [
      {
        "amount": {
          "currency": "RUB",
          "value": 3000
        },
        "completed_at": "2025-12-15 13:47:18.40332 +0000 UTC",
        "created_at": "2025-12-15 13:57:16.036595 +0000 UTC",
        "external_transaction_id": "167590",
        "id": "1fsdfb0b6-fsdf10-4215-99asd-3d18dsdfc7832",
        "payout_method": {
          "sbp": {
            "bank_member_id": "100000000011"
          }
        },
        "rrn": "535146065319",
        "status": "approve",
        "type": "credit"
      }
    ],
    "request_id": "1asdfdb1f-c9be-4e55-12cf-2hgf172faf148",
    "shop_order_id": "2317149"
  },
  "request_id": "0359458gdhc882ce2-asdfgds8cf2977"
}`,
            },
        ],
        status: {
            path: function (id) {
                return "/fins/payouts/status/" + (id || "<transactionId|payoutId>");
            },
            success: `{
  "ok": true,
  "data": {
    "status": "success",
    "payout_id": "b0c28d56-3b3c-4d2b-8c5a-0d33d4a0cb41",
    "shop_order_id": "123456",
    "operations": [
      { "status": "success", "created_at": "2025-10-02T12:42:07+00:00", "rrn": "123456789012" }
    ]
  }
}`,
        },
        callbackSample: `{
  "ok": true,
  "transactionId": 123456,
  "payoutId": "b0c28d56-3b3c-4d2b-8c5a-0d33d4a0cb41",
  "status": "success",
  "provider": "finstar",
  "data": { /* claims провайдера */ }
}`,
        errorSamples: `// Ошибки адаптера ФИНС

// 400 — валидация обязательных полей получателя/документа
{
  "ok": false,
  "error": {
    "code": "bad_request",
    "message": "recipient.document_number and recipient.document_date are required",
    "where": "local"
  }
}
// Пример для отчества:
{
  "ok": false,
  "error": {
    "code": "bad_request",
    "message": "recipient.has_middle_name is required (must be false) when recipient.middle_name is absent",
    "where": "local"
  }
}

// 402 — недостаточно средств в провайдере
{
  "ok": false,
  "error": {
    "code": "insufficient_provider_balance",
    "message": "Insufficient provider balance for card payout.",
    "where": "provider",
    "details": { "available": 1000, "requested": 3000 }
  }
}

// 502 — ошибка провайдера (детали в error.details)
{
  "ok": false,
  "error": {
    "code": "provider_error",
    "message": "...",
    "where": "provider",
    "provider_http_code": 422,
    "details": { /* тело провайдера */ }
  }
}

// 401/403 — аутентификация
{
  "ok": false,
  "error": {
    "code": "access_denied",
    "message": "Merchant auth failed.",
    "where": "local"
  }
}`,
    },

    /* === ВЫПЛАТЫ (CRM Payout Requests) === */
    payouts: {
        key: "payouts",
        label: "Выплаты",
        baseUrlProd: "https://api.e-clear.cc",
        baseUrlTest: "https://api.test.e-clear.cc",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
                {" "}или HMAC (<Kbd>Merchant</Kbd> + <Kbd>Sign</Kbd>)
            </span>
        ),
        supportsStatus: false,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "create_card",
                label: "Создать заявку (карта)",
                path: "/api/v2/payout-request",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "amount": 1235.00,
  "currency": "RUB",
  "type": "card",
  "cardPan": "5469380076360360",
  "fullName": "Иванов Михаил Иванович",
  "email": "test@example.com",
  "phone": "+79001234567",
  "externalReference": "TEST-001",
  "callbackUrl": "https://merchant.example/callback",
  "description": "Выплата вознаграждения",
  "documentDate": "19.02.2026",
  "documentNumber": "PSA-12345"
}`;
                },
                successManual: `{
  "id": "6d19dc25-3213-4165-b0f7-834e995dbb50",
  "status": "created",
  "statusCode": 0,
  "transactionId": 1224,
  "message": "Request accepted"
}`,
            },
            {
                key: "create_sbp",
                label: "Создать заявку (СБП)",
                path: "/api/v2/payout-request",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "amount": 1235.00,
  "currency": "RUB",
  "type": "sbp",
  "phone": "+79001234567",
  "bankMemberId": "100000000111",
  "externalReference": "SBP-TEST-001",
  "callbackUrl": "https://merchant.example/callback"
}`;
                },
                successManual: `{
  "id": "6d19dc25-3213-4165-b0f7-834e995dbb50",
  "status": "created",
  "statusCode": 0,
  "payoutType": "sbp",
  "transactionId": 1224,
  "message": "Request accepted"
}`,
            },
            {
                key: "status",
                label: "Проверить статус",
                path: "/api/v2/payout-request/status",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "id": "6d19dc25-3213-4165-b0f7-834e995dbb50"
  // или по externalReference:
  // "externalReference": "TEST-001"
}`;
                },
                successManual: `{
  "id": "6d19dc25-3213-4165-b0f7-834e995dbb50",
  "status": "awaitingPayout",
  "statusCode": 7,
  "amount": 1235.00,
  "currency": "RUB",
  "payoutType": "sbp",
  "bankName": "Сбер Банк",
  "bankCode": "100000000111",
  "externalReference": "TEST-001",
  "transactionId": 1224,
  "declineReason": null,
  "createdAt": "2026-02-19T12:00:00+00:00",
  "updatedAt": "2026-02-19T12:01:15+00:00"
}`,
            },
        ],
        callbackSample: `// POST на ваш callbackUrl
// Заголовок Sign: HMAC-SHA256 от тела, ключ — ваш merchant key
{
  "data": {
    "payoutRequestId": "6d19dc25-3213-4165-b0f7-834e995dbb50",
    "status": "success",
    "statusCode": 5,
    "amount": 1235.00,
    "currency": "RUB",
    "externalReference": "TEST-001",
    "transactionId": 1224,
    "declineReason": null
  }
}`,
        errorSamples: `// 400 Bad Request — отсутствуют обязательные поля
{
  "status": "error",
  "message": "amount is required"
}

// 400 Bad Request — невалидная карта
{
  "status": "error",
  "message": "Invalid card number"
}

// 400 Bad Request — дубликат externalReference
{
  "status": "error",
  "message": "Payout request with this externalReference already exists"
}

// 400 Bad Request — банк СБП не найден или название неоднозначно
{
  "status": "error",
  "message": "Unknown or ambiguous SBP bank name: Неизвестный банк"
}

// 400 Bad Request — невалидный JSON
{
  "status": "error",
  "message": "Invalid JSON body"
}

// 401 Unauthorized — неверные учётные данные
{
  "message": "Authentication Required"
}

// 404 Not Found — заявка не найдена (проверка статуса)
{
  "status": "error",
  "message": "Payout request not found"
}

// 200 OK — заблокировано антифродом
{
  "id": "6d19dc25-3213-4165-b0f7-834e995dbb50",
  "status": "declined",
  "statusCode": 2,
  "transactionId": null,
  "message": "Blocked by antifraud check",
  "antifraudCode": "card_pan",
  "antifraudProvider": "internal"
}`,
    },

    /* === INTERNAL (Внутренние методы) === */
    internal: {
        key: "internal",
        label: "Внутренние",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: false,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "terminals",
                label: "Список терминалов",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "method": { "type": "balance", "direction": "terminals" }
}`,
                successManual: `{
  "terminals": [
    {
      "terminalId": 123,
      "name": "Terminal SBP",
      "currency": "RUB",
      "balance": 15000.50,
      "transactionBalance": 1500050,
      "transactionBalanceFloat": 15000.50,
      "settlementBalance": 500000,
      "settlementBalanceFloat": 5000.00,
      "actualBalance": 1000050,
      "actualBalanceFloat": 10000.50
    },
    {
      "terminalId": 456,
      "name": "Terminal Card",
      "currency": "RUB",
      "balance": 8500.00,
      "transactionBalance": 850000,
      "transactionBalanceFloat": 8500.00,
      "settlementBalance": 200000,
      "settlementBalanceFloat": 2000.00,
      "actualBalance": 650000,
      "actualBalanceFloat": 6500.00
    }
  ]
}`,
            },
            {
                key: "terminal_balance",
                label: "Баланс терминала",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "method": { "type": "balance", "direction": "terminal" },
  "terminalId": 123
}`,
                successManual: `{
  "terminal": {
    "terminalId": 123,
    "name": "Terminal SBP",
    "currency": "RUB",
    "balance": 15000.50,
    "transactionBalance": 1500050,
    "transactionBalanceFloat": 15000.50,
    "settlementBalance": 500000,
    "settlementBalanceFloat": 5000.00,
    "actualBalance": 1000050,
    "actualBalanceFloat": 10000.50
  }
}`,
            },
            {
                key: "balance_total",
                label: "Общий баланс",
                path: "/v1",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "method": { "type": "balance" }
}`,
                successManual: `{
  "balance": [
    { "currency": "RUB", "value": 23500.50 }
  ],
  "providers": {
    "a1b2c3d4-e5f6-...": [
      { "currency": "RUB", "value": 15000.50 }
    ],
    "b2c3d4e5-f6g7-...": [
      { "currency": "RUB", "value": 8500.00 }
    ]
  },
  "balances": {
    "items": [
      {
        "terminal_id": 123,
        "terminal_name": "Terminal SBP",
        "merchant_settings_id": 456,
        "transactionBalance": 1500050,
        "transactionBalanceFloat": 15000.50,
        "settlementBalance": 500000,
        "settlementBalanceFloat": 5000.00,
        "actualBalance": 1000050,
        "actualBalanceFloat": 10000.50
      },
      {
        "terminal_id": 789,
        "terminal_name": "Terminal Card",
        "merchant_settings_id": 101,
        "transactionBalance": 850000,
        "transactionBalanceFloat": 8500.00,
        "settlementBalance": 200000,
        "settlementBalanceFloat": 2000.00,
        "actualBalance": 650000,
        "actualBalanceFloat": 6500.00
      }
    ],
    "totals": {
      "transactionBalance": 2350050,
      "transactionBalanceFloat": 23500.50,
      "settlementBalance": 700000,
      "settlementBalanceFloat": 7000.00,
      "actualBalance": 1650050,
      "actualBalanceFloat": 16500.50
    }
  }
}`,
            },
        ],
        callbackSample: null,
        errorSamples: `// 400 Bad Request — отсутствует обязательное поле terminalId
{ "code": "200", "message": "terminalId is required" }

// 404 Not Found — терминал не найден или принадлежит другому мерчанту
{ "code": "200", "message": "Terminal not found." }

// 401 Unauthorized — неверный UUID или ключ мерчанта
{ "message": "Invalid merchant credentials" }

// 500 Internal Server Error
{ "code": "100", "message": "Internal server error" }`,
    },

    /* === ANTIFRAUD (Проверка на фрод) === */
    antifraud: {
        key: "antifraud",
        label: "Антифрод",
        baseUrlProd: "https://api.e-clear.cc/api/v2/antifraud",
        baseUrlTest: "https://api.test.e-clear.cc/api/v2/antifraud",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: false,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "check",
                label: "Проверка на фрод",
                path: "/check",
                bodyWithIp: null,
                bodyWithoutIp: () => `{
  "cardPan": "4111111111111111",
  "phone": "79001234567",
  "clientIp": "203.0.113.17"
}`,
                successManual: `// Проверка пройдена (HTTP 200)
{
  "status": "ok",
  "checkTimeMs": 45
}

// Заблокировано (HTTP 422)
{
  "status": "blocked",
  "reason": "Карта в чёрном списке",
  "blockedKind": "card_pan"
}`,
            },
        ],
        callbackSample: null,
        errorSamples: `// 400 Bad Request — не передано ни одного поля для проверки
{ "error": "validation_error", "message": "At least one of cardPan, phone, clientIp is required" }

// 401 Unauthorized — неверный UUID или ключ мерчанта
{ "error": "unauthorized", "message": "Invalid merchant credentials" }

// 422 Unprocessable Entity — карта/телефон/IP заблокирован
{
  "status": "blocked",
  "reason": "Карта в чёрном списке",
  "blockedKind": "card_pan"
}

// 500 Internal Server Error
{ "error": "internal_error", "message": "Internal server error" }`,
    },

    /** === NPAY — приём платежей по QR (Кыргызстан, npay.kg) === */
    npay: {
        key: "npay",
        label: "NPay (QR)",
        baseUrlProd: "https://api.e-clear.cc/api",
        baseUrlTest: "https://api.test.e-clear.cc/api",
        authHelp: (
            <span>
                <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>
            </span>
        ),
        supportsStatus: true,
        headers: [
            { name: "Content-Type", value: "application/json" },
            { name: "X-Merchant-UUID", value: "<YOUR-UUID>" },
            { name: "X-Merchant-Key", value: "<YOUR-KEY>" },
        ],
        methods: [
            {
                key: "payment",
                label: "Создать платёж",
                path: "/npay/payments",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "amount": 100,
  "external_reference": "order-123",
  "comment": "Оплата заказа #123",
  "redirect_url": "https://merchant.example/success",
  "callback_url": "https://merchant.example/callback",
  "amount_can_be_changed": false,
  "expire_at": "2026-06-25T18:00:00+03:00"
}`;
                },
                successManual: `{
  "ok": true,
  "transactionId": 281,
  "npayGuid": "6a4cd87f-d1c8-4e55-8928-17cd37ce1a07",
  "payload": "https://pay.namba.kg/#00020101...6304XXXX",
  "paymentUrl": "https://pay.namba.kg/#00020101...6304XXXX",
  "qrBase64": "iVBORw0KGgoAAAANSUhEUg...",
  "qrDataUri": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUg...",
  "paymentPageUrl": "https://my.e-clear.cc/npay/3f8c9a1b...d21",
  "amount": 100,
  "amountRub": 100,
  "amountKgs": 112.45,
  "rate": 1.1245,
  "currency": "RUB",
  "expireAt": "2026-06-25T18:00:00+03:00",
  "status": "PROCESSING",
  "request_id": "abc123..."
}`,
                successLink: null,
            },
            {
                key: "payment_status",
                label: "Статус платежа",
                path: "/npay/payments/<transactionId>",
                bodyWithIp: null,
                bodyWithoutIp: null,
                successManual: `{
  "ok": true,
  "transactionId": 281,
  "npayGuid": "6a4cd87f-d1c8-4e55-8928-17cd37ce1a07",
  "status": "SUCCESS",
  "providerStatus": null,
  "amount": 100,
  "currency": "RUB",
  "request_id": "abc123..."
}`,
                successLink: null,
            },
            {
                key: "page",
                label: "Данные страницы оплаты",
                path: "/npay/page/<token>",
                bodyWithIp: null,
                bodyWithoutIp: null,
                successManual: `{
  "ok": true,
  "status": "PROCESSING",
  "payload": "https://pay.namba.kg/#00020101...6304XXXX",
  "qrBase64": "iVBORw0KGgoAAAANSUhEUg...",
  "qrDataUri": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUg...",
  "amountRub": 100,
  "amountKgs": 112.45,
  "currency": "RUB",
  "expireAt": "2026-06-25T18:00:00+03:00",
  "expireAtTs": 1782313080,
  "request_id": "abc123..."
}`,
                successLink: null,
            },
            {
                key: "refund",
                label: "Создать возврат",
                path: "/npay/refunds",
                bodyWithIp: null,
                bodyWithoutIp: function () {
                    return `{
  "transaction_id": 281,
  "amount": 100,
  "comment": "Возврат по запросу клиента",
  "callback_url": "https://merchant.example/refund-callback"
}`;
                },
                successManual: `{
  "ok": true,
  "transactionId": 282,
  "parentTransactionId": 281,
  "npayRefundGuid": "b2c3d4e5-f6a7-8901-bcde-f12345678901",
  "amount": 100,
  "amountRub": 100,
  "amountKgs": 112.45,
  "rate": 1.1245,
  "currency": "RUB",
  "status": "pending",
  "request_id": "abc123..."
}`,
                successLink: null,
            },
            {
                key: "refund_status",
                label: "Статус возврата",
                path: "/npay/refunds/<transactionId>",
                bodyWithIp: null,
                bodyWithoutIp: null,
                successManual: `{
  "ok": true,
  "transactionId": 282,
  "status": "SUCCESS",
  "providerStatus": "COMPLETED",
  "request_id": "abc123..."
}`,
                successLink: null,
            },
        ],
        status: {
            path: function (id) {
                return "/npay/payments/" + (id || "<transactionId>");
            },
            success: `{
  "ok": true,
  "transactionId": 281,
  "npayGuid": "6a4cd87f-d1c8-4e55-8928-17cd37ce1a07",
  "status": "SUCCESS",
  "providerStatus": null,
  "amount": 100,
  "currency": "RUB",
  "request_id": "abc123..."
}`,
        },
        callbackSample: `// POST на ваш callback_url при смене статуса платежа.
// Сумма — в РУБЛЯХ (валюта вашего платежа), как при создании.
{
  "transactionId": 281,
  "externalReference": "order-123",
  "state": "SUCCESS",
  "providerStatus": "COMPLETED",
  "amount": 100,
  "amountRub": 100,
  "currency": "RUB",
  "createdAt": "2026-06-25T10:30:00+03:00"
}

// state: SUCCESS | DECLINED | PENDING`,
        webhookSample: `// Это вебхук ОТ NPay НА НАШ сервер — обрабатываем мы, вам делать ничего не нужно.
// Приведён для понимания, как обновляется статус. Вам приходит callback (см. слева).
// POST /webhooks/npay/{token}
// Тип PAYMENT_ORDER (платёж):
{
  "type": "PAYMENT_ORDER",
  "data": {
    "externalId": "281",
    "status": "COMPLETED",
    "guid": "6a4cd87f-d1c8-4e55-8928-17cd37ce1a07",
    "amount": 11245
  }
}

// Тип REFUND_ORDER (возврат):
{
  "type": "REFUND_ORDER",
  "data": {
    "externalId": "282",
    "status": "COMPLETED",
    "guid": "b2c3d4e5-f6a7-8901-bcde-f12345678901",
    "amount": 11245
  }
}`,
        errorSamples: `// 400 — отсутствуют/некорректные поля
{
  "ok": false,
  "error": { "code": "bad_request", "message": "amount is required and must be numeric" }
}

// 400 — нет NPay настроек для мерчанта
{
  "ok": false,
  "error": { "code": "bad_request", "message": "No NPay settings found for this merchant." }
}

// 403 — неверные учётные данные
{
  "ok": false,
  "error": { "code": "access_denied", "message": "Merchant auth failed." }
}

// 404 — страница оплаты не найдена (неверный/просроченный token)
{
  "ok": false,
  "error": { "code": "not_found", "message": "Payment not found" }
}

// 500 — ошибка NPay провайдера
{
  "ok": false,
  "error": { "code": "internal_error", "message": "NPay createOneTimeLink failed: ..." }
}`,
    },
};

/** Страница */
const MerchantsDocs = () => {
    const [providerKey, setProviderKey] = useState("hydra");
    const [methodKey, setMethodKey] = useState("p2p");

    const provider = PROVIDERS[providerKey];

    const method = useMemo(() => {
        const list = provider && provider.methods ? provider.methods : [];
        let found = null;
        for (let i = 0; i < list.length; i++) {
            if (list[i].key === methodKey) {
                found = list[i];
                break;
            }
        }
        if (!found && list.length > 0) found = list[0];
        return found;
    }, [providerKey, methodKey, provider]);

    const createCurlWithIp = useMemo(() => {
        if (!provider || !method || !method.bodyWithIp) return "";
        const base = provider.baseUrlTest;
        const bodyStr = method.bodyWithIp(method.label) || "";
        return buildCurlPOST(base, method.path, provider.headers, bodyStr);
    }, [provider, method]);

    const createCurlWithoutIp = useMemo(() => {
        if (!provider || !method || !method.bodyWithoutIp) return "";
        const base = provider.baseUrlTest;
        const bodyStr = method.bodyWithoutIp(method.label) || "";
        return buildCurlPOST(base, method.path, provider.headers, bodyStr);
    }, [provider, method]);

    const statusCurl = useMemo(() => {
        if (!provider || !provider.supportsStatus) return "";
        const base = provider.baseUrlTest;
        const path = provider.status.path("<id>");
        return buildCurlGET(base, path, provider.headers);
    }, [provider]);

    // Публичный GET страницы оплаты (NPay) — БЕЗ заголовков аутентификации.
    const npayPageCurl = useMemo(() => {
        if (!provider || providerKey !== "npay") return "";
        return buildCurlGET(provider.baseUrlTest, "/npay/page/<token>", []);
    }, [provider, providerKey]);


    return (
        <div className={s.page}>
            <main className={s.container}>
                <section className={s.hero}>
                    <h1 className={s.h1}>API для клиентов</h1>
                    <p className={s.lead}>
                        Приём платежей: <strong>Craft</strong> (QR),{" "}
                        <strong>Hydra</strong> (P2P, СБП).{" "}
                        <strong>Выплаты</strong> на карту.{" "}
                        Проверка карт: <strong>Антифрод</strong>.
                    </p>
                    <div className={s.apiV3Banner}>
                        <div>
                            <strong>Единый API v3</strong>
                            <span>
                                Новая эталонная интеграция: все методы, единые форматы и тестирование запросов.
                            </span>
                        </div>
                        <a className={s.apiV3Link} href={API_V3_DOCS_URL}>
                            Открыть интерактивную документацию
                        </a>
                    </div>
                    <div className={s.meta}>
                        <span>
                            Prod: <code>{provider ? provider.baseUrlProd : ""}</code>
                        </span>
                        <span>
                            Test: <code>{provider ? provider.baseUrlTest : ""}</code>
                        </span>
                        <span>
                            Content-Type: <code>application/json</code>
                        </span>
                        <span>Auth: {provider ? provider.authHelp : null}</span>
                    </div>

                    {providerKey === "craft" && (
                        <div className={s.note}>
                            <strong>Craft</strong> — приём платежей через QR-код.
                        </div>
                    )}
                    {providerKey === "fins" && (
                        <div className={s.note}>
                            Идемпотентность обеспечивает адаптер — отдельный{" "}
                            <Kbd>X-Idempotency-Key</Kbd> передавать не нужно. Для ваших дублей
                            используйте поле <code>external_reference</code>.
                        </div>
                    )}
                    {providerKey === "tkb" && (
                        <div className={s.note}>
                            Все операции выполняются через единый POST{" "}
                            <code>/api/v1</code> с указанием <code>method.type</code>.
                            Для выплат TKB достаточно <code>card.number</code>; данные{" "}
                            <code>exp</code>/<code>cvv</code> не требуются.
                        </div>
                    )}
                    {providerKey === "payouts" && (
                        <div className={s.note}>
                            Создание заявок на выплату на банковскую карту или по СБП.
                            Аутентификация через <Kbd>X-Merchant-UUID</Kbd> + <Kbd>X-Merchant-Key</Kbd>{" "}
                            или HMAC (<Kbd>Merchant</Kbd> + <Kbd>Sign</Kbd>).
                            При смене статуса на <code>callbackUrl</code> отправляется уведомление с подписью <Kbd>Sign</Kbd>.
                        </div>
                    )}
                    {providerKey === "internal" && (
                        <div className={s.note}>
                            Внутренние методы для работы с терминалами и балансами.
                            Аутентификация через <Kbd>X-Merchant-UUID</Kbd> и <Kbd>X-Merchant-Key</Kbd>.
                            Возвращаются только <strong>активные</strong> терминалы.
                        </div>
                    )}
                    {providerKey === "npay" && (
                        <div className={s.note}>
                            Приём платежей по QR-коду (Кыргызстан, <strong>npay.kg</strong>).
                            Вы передаёте сумму <strong>в рублях</strong> (<code>amount</code>, <code>currency: RUB</code>) —
                            мы автоматически конвертируем её в сомы (KGS) по актуальному курсу
                            и формируем QR. Клиент платит, отсканировав QR в приложении банка
                            (Сбербанк, Тинькофф и др.). Результат вы получаете на свой{" "}
                            <code>callback_url</code>; статус также доступен по{" "}
                            <code>GET /npay/payments/{"<transactionId>"}</code>.
                        </div>
                    )}
                </section>

                {/* Переключатель провайдера */}
                <nav className={s.tabs} role="tablist" aria-label="Интеграция">
                    {Object.keys(PROVIDERS).filter(k => !['tkb', 'fins'].includes(k)).map((k) => {
                        const active = providerKey === k;
                        const classes = active ? `${s.tab} ${s.tabActive}` : s.tab;
                        return (
                            <button
                                key={k}
                                role="tab"
                                aria-selected={active}
                                className={classes}
                                onClick={() => {
                                    setProviderKey(k);
                                    const list =
                                        PROVIDERS[k] && PROVIDERS[k].methods
                                            ? PROVIDERS[k].methods
                                            : [];
                                    const first = list.length > 0 ? list[0].key : null;
                                    if (first) setMethodKey(first);
                                }}
                            >
                                {PROVIDERS[k].label}
                            </button>
                        );
                    })}
                </nav>

                {/* Переключатель метода */}
                <nav className={s.tabs} role="tablist" aria-label="Методы">
                    {(provider && provider.methods ? provider.methods : []).map((m) => {
                        const active = method && method.key === m.key;
                        const classes = active ? `${s.tab} ${s.tabActive}` : s.tab;
                        return (
                            <button
                                key={m.key}
                                role="tab"
                                aria-selected={active}
                                className={classes}
                                onClick={() => setMethodKey(m.key)}
                            >
                                {m.label}
                            </button>
                        );
                    })}
                </nav>

                {/* Как это работает */}
                <section className={s.card}>
                    <h2 className={s.h2}>Как это работает</h2>
                    {providerKey === "craft" ? (
                        <ol className={s.ol}>
                            <li>
                                <code>POST /craft/payment</code> — создание платежа.
                            </li>
                            <li>
                                <strong>H2H</strong> (<code>h2h: true</code>): возвращает QR-ссылку в <code>externalRefs.payload</code>.
                            </li>
                            <li>
                                <strong>Widget</strong> (без <code>h2h</code>): возвращает <code>redirectUrl</code> на платёжную страницу.
                            </li>
                            <li>
                                <code>GET /craft/status/{"{transactionId}"}</code> — проверка статуса.
                            </li>
                            <li>
                                Callback приходит на <code>callbackUrl</code> при смене статуса.
                            </li>
                        </ol>
                    ) : providerKey === "hydra" ? (
                        <ol className={s.ol}>
                            <li>
                                Отправьте <code>POST {method ? method.path : ""}</code> с телом
                                и заголовками.
                            </li>
                            <li>
                                Если передан <code>customerIp</code> — вернём{" "}
                                <code>externalRefs</code> и идентификаторы.
                            </li>
                            <li>
                                Без <code>customerIp</code> — вернём ссылку (
                                <code>redirectUrl</code> или <code>paymentUrl</code>).
                            </li>
                            <li>
                                Проверка состояния:{" "}
                                <code>
                                    GET {provider ? provider.status.path("{transactionId}") : ""}
                                </code>
                                .
                            </li>
                            <li>
                                Если передан <code>callbackUrl</code> — отправим серверное
                                уведомление.
                            </li>
                        </ol>
                    ) : providerKey === "tkb" ? (
                        <ol className={s.ol}>
                            <li>
                                Единый роут: <code>POST /api/v1</code>.
                            </li>
                            <li>
                                В запросе укажите <code>method.type</code>:
                                <ul className={s.ul}>
                                    <li>
                                        <code>payout</code> — создание выплаты (провайдер{" "}
                                        <code>tkb</code>).
                                    </li>
                                    <li>
                                        <code>balance</code> — получить баланс мерчанта.
                                    </li>
                                    <li>
                                        <code>status</code> — получить статус/ы по{" "}
                                        <code>transactionId</code> или{" "}
                                        <code>externalReference</code>.
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Результаты колбэков приходят на ваш{" "}
                                <code>callbackUrl</code> (если задан).
                            </li>
                        </ol>
                    ) : providerKey === "fins" ? (
                        <ol className={s.ol}>
                            <li>
                                Создание выплаты (через наш адаптер):
                                <ul className={s.ul}>
                                    <li>
                                        карта: <code>POST /fins/payouts/card</code>
                                    </li>
                                    <li>
                                        СБП: <code>POST /fins/payouts/sbp</code>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Идемпотентность — внутри адаптера;{" "}
                                <code>X-Idempotency-Key</code> передавать не нужно. Для своих
                                дублей используйте <code>external_reference</code>.
                            </li>
                            <li>
                                В <code>recipient</code> ОБЯЗАТЕЛЬНО укажите:{" "}
                                <code>last_name</code>, <code>first_name</code>,{" "}
                                <code>document_number</code> (номер документа, подтверждающего выплату — номер ПСА),{" "}
                                <code>document_date</code> (дата документа, подтверждающего выплату в формате YYYY-MM-DD или DD.MM.YYYY) и <code>middle_name</code>{" "}
                                <em>или</em> <code>has_middle_name=false</code> (если отчества
                                нет).
                            </li>
                            <li>
                                Проверка статуса:
                                <br />
                                <code>
                                    GET /fins/payouts/status/&lt;transactionId|payoutId&gt;
                                </code>
                            </li>
                            <li>
                                Проверка баланса: <code>GET /fins/balance</code>.
                            </li>
                        </ol>
                    ) : providerKey === "payouts" ? (
                        <ol className={s.ol}>
                            <li>
                                Отправьте <code>POST /api/v2/payout-request</code> с данными получателя и суммой.
                            </li>
                            <li>
                                В ответе — UUID заявки, статус и <code>transactionId</code>.
                            </li>
                            <li>
                                Заявка проходит антифрод-проверку. Если заблокирована — <code>status: "declined"</code>.
                            </li>
                            <li>
                                Проверка статуса: <code>POST /api/v2/payout-request/status</code> по <code>id</code> или <code>externalReference</code>.
                            </li>
                            <li>
                                Если задан <code>callbackUrl</code> — при каждой смене статуса на ваш URL придёт POST с подписью <Kbd>Sign</Kbd> (HMAC-SHA256).
                            </li>
                        </ol>
                    ) : providerKey === "internal" ? (
                        <ol className={s.ol}>
                            <li>
                                Все запросы через единый <code>POST /api/v1</code>.
                            </li>
                            <li>
                                Аутентификация: <code>X-Merchant-UUID</code> + <code>X-Merchant-Key</code>.
                            </li>
                            <li>
                                <code>method.direction: "terminals"</code> — список всех активных терминалов с балансами.
                            </li>
                            <li>
                                <code>method.direction: "terminal"</code> + <code>terminalId</code> — баланс конкретного терминала.
                            </li>
                            <li>
                                Без <code>direction</code> — общий баланс по всем терминалам.
                            </li>
                            <li>
                                Возвращаются только <strong>активные</strong> терминалы (enabled + status=active).
                            </li>
                            <li>
                                Во всех вариантах запроса возвращаются <strong>три баланса</strong>:{" "}
                                <code>transactionBalance</code> (транзакционный),{" "}
                                <code>settlementBalance</code> (выплаченный),{" "}
                                <code>actualBalance</code> (фактический).
                                Суммы в копейках, <code>*Float</code>-версии — в рублях.
                            </li>
                        </ol>
                    ) : providerKey === "antifraud" ? (
                        <ol className={s.ol}>
                            <li>
                                Отправьте <code>POST /check</code> с данными для проверки.
                            </li>
                            <li>
                                Обязательно передать хотя бы одно из: <code>cardPan</code>, <code>phone</code>, <code>clientIp</code>.
                            </li>
                            <li>
                                HTTP 200 — проверка пройдена, HTTP 422 — заблокировано.
                            </li>
                        </ol>
                    ) : providerKey === "npay" ? (
                        <ol className={s.ol}>
                            <li>
                                Создайте платёж: <code>POST /npay/payments</code> с суммой в рублях.
                                В ответе получите <code>transactionId</code>, готовый QR
                                (<code>qrDataUri</code> / <code>qrBase64</code>) и ссылку на
                                страницу оплаты <code>paymentPageUrl</code>.
                            </li>
                            <li>
                                Покажите клиенту QR одним из способов:
                                <ul className={s.ul}>
                                    <li>
                                        <strong>Проще всего:</strong> дайте ссылку{" "}
                                        <code>paymentPageUrl</code> — это наша готовая страница
                                        с QR-кодом, суммой и таймером.
                                    </li>
                                    <li>
                                        <strong>Своя страница:</strong> вставьте QR картинкой —{" "}
                                        <code>{'<img src="{qrDataUri}">'}</code>. Либо нарисуйте QR
                                        сами из строки <code>payload</code>.
                                    </li>
                                </ul>
                            </li>
                            <li>
                                Клиент сканирует QR в приложении банка и оплачивает.
                            </li>
                            <li>
                                Узнать результат: на ваш <code>callback_url</code> придёт
                                уведомление (если задан), и/или опрашивайте{" "}
                                <code>GET /npay/payments/{"<transactionId>"}</code>.
                            </li>
                            <li>
                                Возврат: <code>POST /npay/refunds</code> с{" "}
                                <code>transaction_id</code> и <code>amount</code> (тоже в рублях).
                            </li>
                        </ol>
                    ) : null}
                </section>


                {/* Тело запроса */}
                <section className={s.card}>
                    <h3 className={s.h3}>Тело запроса</h3>

                    {providerKey === "craft" ? (
                        <div>
                            <h4 className={s.h4}>Обязательные поля</h4>
                            <ul className={s.ul}>
                                <li><code>client</code> — идентификатор клиента (телефон, email и т.п.)</li>
                                <li><code>product</code> — название товара/услуги</li>
                                <li><code>sum</code> — сумма в рублях</li>
                            </ul>

                            <h4 className={s.h4}>Опциональные поля</h4>
                            <ul className={s.ul}>
                                <li><code>h2h</code> — <code>true</code> для получения QR-ссылки, иначе редирект</li>
                                <li><code>description</code> — описание платежа</li>
                                <li><code>externalReference</code> — ваш внешний идентификатор заказа</li>
                                <li><code>callbackUrl</code> — URL для уведомлений о статусе</li>
                                <li><code>redirectUrlSuccess</code> — URL редиректа при успехе (для Widget)</li>
                                <li><code>redirectUrlError</code> — URL редиректа при ошибке (для Widget)</li>
                            </ul>

                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    ) : providerKey === "hydra" ? (
                        <div>
                            <p className={s.note}>
                                Валюта <code>RUB</code>. <code>amount</code> — целое число в
                                рублях.
                                <br /> <code>successUrl</code>, <code>declineUrl</code>,{" "}
                                <code>callbackUrl</code> — опциональны.
                            </p>

                            <h4 className={s.h4}>
                                Вариант A с <code>customerIp</code>
                            </h4>
                            <div className={s.note}>
                                Вернутся реквизиты externalRefs и идентификаторы.
                            </div>
                            <Code lang="json">
                                {method ? method.bodyWithIp(method.label) : "{}"}
                            </Code>

                            <h4 className={s.h4}>
                                Вариант B без <code>customerIp</code>
                            </h4>
                            <div className={s.note}>
                                Вернётся ссылка на оплату (<code>redirectUrl</code> или{" "}
                                <code>paymentUrl</code>).
                            </div>
                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    ) : providerKey === "tkb" ? (
                        <div>
                            <p className={s.note}>
                                Все операции через <code>POST /api/v1</code>. В теле обязательно{" "}
                                <code>method.type</code>. Для выплат используйте{" "}
                                <code>
                                    {"{ method: { type: \"payout\", provider: \"tkb\" } }"}
                                </code>
                                .
                            </p>
                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    ) : providerKey === "fins" ? (
                        <div>
                            <p className={s.note}>
                                Валюта <code>RUB</code>. <code>amount</code> — число в рублях.{" "}
                                <code>external_reference</code> — строка для вашей корреляции.
                                <br />
                                Для получателя обязательны:{" "}
                                <code>last_name</code>, <code>first_name</code>,{" "}
                                <code>document_number</code> (номер документа, подтверждающего выплату — номер ПСА),{" "}
                                <code>document_date</code> (дата ПСА) и <code>middle_name</code>{" "}
                                <em>или</em> <code>has_middle_name=false</code>. Поля{" "}
                                <code>inn</code>, <code>purpose</code>, <code>reason</code>,{" "}
                                <code>email</code>, <code>phone</code> — необязательные (для
                                СБП телефон обязателен в блоке <code>sbp.phone</code>).{" "}
                                <code>document_date</code> допускает форматы{" "}
                                <code>YYYY-MM-DD</code> (ISO 8601) и <code>DD.MM.YYYY</code> — конвертируется во внутренний формат API.
                            </p>
                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    ) : providerKey === "payouts" ? (
                        <div>
                            {methodKey === "status" ? (
                                <React.Fragment>
                                    <h4 className={s.h4}>Параметры поиска</h4>
                                    <p className={s.note}>
                                        Передайте <code>id</code> заявки или её <code>externalReference</code>.
                                    </p>
                                </React.Fragment>
                            ) : (
                            <React.Fragment>
                            <h4 className={s.h4}>Обязательные поля</h4>
                            <ul className={s.ul}>
                                <li><code>amount</code> — сумма в рублях (число, напр. <code>1235.00</code>)</li>
                                {methodKey === "create_sbp" ? (
                                    <React.Fragment>
                                        <li><code>type</code> — значение <code>"sbp"</code></li>
                                        <li><code>phone</code> — российский мобильный номер получателя</li>
                                        <li>банк — <code>bankName</code> (название) либо <code>bankMemberId</code> (12-значный код НСПК)</li>
                                    </React.Fragment>
                                ) : (
                                    <li><code>cardPan</code> — номер карты (13-19 цифр, проверяется по Luhn)</li>
                                )}
                            </ul>

                            {methodKey === "create_sbp" && (
                                <p className={s.note}>
                                    Можно передать название банка или сразу код НСПК. Если переданы оба поля,
                                    они должны обозначать один банк; неизвестное значение вернёт <code>400 Bad Request</code>.
                                </p>
                            )}

                            <h4 className={s.h4}>Опциональные поля</h4>
                            <ul className={s.ul}>
                                {methodKey !== "create_sbp" && <li><code>type</code> — тип выплаты (<code>"card"</code>, по умолчанию <code>"card"</code>)</li>}
                                <li><code>currency</code> — валюта (<code>"RUB"</code>, по умолчанию <code>"RUB"</code>)</li>
                                <li><code>fullName</code> — ФИО получателя; пустое или отсутствующее значение сохраняется как <code>"0"</code></li>
                                <li><code>email</code> — email получателя</li>
                                {methodKey !== "create_sbp" && <li><code>phone</code> — телефон получателя</li>}
                                <li><code>externalReference</code> — ваш внешний ID (до 64 символов, уникальный)</li>
                                <li><code>callbackUrl</code> — URL для уведомлений о смене статуса (до 512 символов)</li>
                                <li><code>description</code> — описание (до 512 символов)</li>
                                <li><code>documentDate</code> — дата документа (до 20 символов)</li>
                                <li><code>documentNumber</code> — номер документа (до 64 символов)</li>
                            </ul>

                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                            </React.Fragment>
                            )}
                            {methodKey === "status" && (
                                <Code lang="json">
                                    {method ? method.bodyWithoutIp(method.label) : "{}"}
                                </Code>
                            )}
                        </div>
                    ) : providerKey === "internal" ? (
                        <div>
                            <p className={s.note}>
                                Все операции через <code>POST /api/v1</code>. Используйте{" "}
                                <code>method.type: "balance"</code> с разными <code>direction</code>.
                            </p>
                            <h4 className={s.h4}>Аутентификация</h4>
                            <ul className={s.ul}>
                                <li><code>X-Merchant-UUID</code> — UUID</li>
                                <li><code>X-Merchant-Key</code> — ключ</li>
                            </ul>

                            <h4 className={s.h4}>1. Получить список всех терминалов</h4>
                            <Code lang="json">{`{
  "method": { "type": "balance", "direction": "terminals" }
}`}</Code>

                            <h4 className={s.h4}>2. Получить баланс конкретного терминала</h4>
                            <Code lang="json">{`{
  "method": { "type": "balance", "direction": "terminal" },
  "terminalId": 123
}`}</Code>

                            <h4 className={s.h4}>3. Получить общий баланс</h4>
                            <Code lang="json">{`{
  "method": { "type": "balance" }
}`}</Code>
                        </div>
                    ) : providerKey === "antifraud" ? (
                        <div>
                            <p className={s.note}>
                                Передайте хотя бы одно из полей:
                            </p>
                            <ul className={s.ul}>
                                <li><code>cardPan</code> — номер карты (13-19 цифр)</li>
                                <li><code>phone</code> — телефон (только цифры)</li>
                                <li><code>clientIp</code> — IP адрес клиента</li>
                            </ul>
                            <Code lang="json">
                                {method ? method.bodyWithoutIp(method.label) : "{}"}
                            </Code>
                        </div>
                    ) : providerKey === "npay" ? (
                        <div>
                            {methodKey === "payment_status" || methodKey === "refund_status" ? (
                                <p className={s.note}>
                                    GET-запрос — тело не нужно. Передайте <code>transactionId</code> в URL.
                                </p>
                            ) : methodKey === "page" ? (
                                <p className={s.note}>
                                    <strong>Публичный</strong> GET-запрос — <strong>без</strong> заголовков
                                    аутентификации. <code>token</code> в URL — это последний сегмент{" "}
                                    <code>paymentPageUrl</code> из ответа создания платежа. Тот же эндпоинт
                                    использует наша страница оплаты. По числовому <code>transactionId</code>{" "}
                                    он <strong>не</strong> работает (защита от подбора чужих платежей).
                                </p>
                            ) : (
                                <div>
                                    <p className={s.note}>
                                        Сумма <code>amount</code> — в <strong>рублях</strong> (число, напр.{" "}
                                        <code>100</code> или <code>100.50</code>). Конвертацию в сомы
                                        делаем мы; <code>currency</code> в ответе — <code>RUB</code>.
                                        {methodKey === "payment" && (
                                            <span> <code>redirect_url</code>, <code>callback_url</code>,{" "}
                                            <code>comment</code>, <code>expire_at</code>,{" "}
                                            <code>amount_can_be_changed</code> — опциональны
                                            (по умолчанию срок жизни ссылки — 30 минут).</span>
                                        )}
                                        {methodKey === "refund" && (
                                            <span> <code>transaction_id</code> — обязательный (id исходного платежа).{" "}
                                            <code>comment</code>, <code>callback_url</code> — опциональны.</span>
                                        )}
                                    </p>
                                    <Code lang="json">
                                        {method && method.bodyWithoutIp ? method.bodyWithoutIp(method.label) : "{}"}
                                    </Code>
                                </div>
                            )}
                        </div>
                    ) : null}
                </section>

                {/* Ответы и примеры cURL */}
                <section className={s.grid2}>
                    <div className={s.card}>
                        <h3 className={s.h3}>Успешный ответ</h3>
                        {providerKey === "npay" ? (
                            <div>
                                <Code lang="json">{method ? method.successManual : "{}"}</Code>
                                {(methodKey === "payment" || methodKey === "refund") && createCurlWithoutIp && (
                                    <div>
                                        <h4 className={s.h4}>Пример cURL</h4>
                                        <Code lang="bash">{createCurlWithoutIp}</Code>
                                    </div>
                                )}
                                {(methodKey === "payment_status" || methodKey === "refund_status") && statusCurl && (
                                    <div>
                                        <h4 className={s.h4}>Пример cURL</h4>
                                        <Code lang="bash">{statusCurl}</Code>
                                    </div>
                                )}
                                {methodKey === "page" && npayPageCurl && (
                                    <div>
                                        <h4 className={s.h4}>Пример cURL</h4>
                                        <Code lang="bash">{npayPageCurl}</Code>
                                    </div>
                                )}
                            </div>
                        ) : providerKey === "hydra" ? (
                            <div>
                                <h4 className={s.h4}>
                                    Если передан <code>customerIp</code> (manual)
                                </h4>
                                <Code lang="json">{method ? method.successManual : "{}"}</Code>
                                <h4 className={s.h4}>Если не передан (redirect)</h4>
                                <Code lang="json">{method ? method.successLink : "{}"}</Code>

                                <h4 className={s.h4}>Пример cURL</h4>
                                <div className={s.note}>
                                    Вариант A с <code>customerIp</code>
                                </div>
                                <Code lang="bash">{createCurlWithIp}</Code>
                                <div className={s.note}>
                                    Вариант B без <code>customerIp</code>
                                </div>
                                <Code lang="bash">{createCurlWithoutIp}</Code>
                            </div>
                        ) : (
                            <div>
                                <Code lang="json">{method ? method.successManual : "{}"}</Code>
                                <h4 className={s.h4}>Пример cURL</h4>
                                <Code lang="bash">{createCurlWithoutIp}</Code>
                            </div>
                        )}
                    </div>

                    <div className={s.card}>
                        <h3 className={s.h3}>Ошибки</h3>
                        {provider && provider.errorSamples ? (
                            <Code lang="json">{provider.errorSamples}</Code>
                        ) : (
                            <Code lang="json">{`// 400 Bad Request — некорректные/отсутствующие поля
{ "message": "объяснение ошибки" }

// 401 или 403 — ошибки аутентификации`}</Code>
                        )}
                    </div>
                </section>

                {/* Проверка баланса (только ФИНС рендерится отдельно; для TKB баланс — через POST /v1) */}
                {providerKey === "fins" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Проверка баланса</h2>
                        <p className={s.note}>
                            Агрегированный баланс по CARD+SBP. Поля{" "}
                            <code>amount_minor</code> / <code>overdraft_minor</code> — в
                            минорных единицах (копейках). Для людей —{" "}
                            <code>amount</code>/<code>overdraft</code> в рублях (2 знака).
                        </p>
                        <h4 className={s.h4}>Пример ответа</h4>
                        <Code lang="json">{`{
  "ok": true,
  "provider": "finstar",
  "balance": {
    "amount": "123.45",
    "overdraft": "0.00",
    "currency": "RUB",
    "amount_minor": 12345,
    "overdraft_minor": 0
  },
  "request_id": "..."
}`}</Code>
                        <h4 className={s.h4}>Пример cURL</h4>
                        <Code lang="bash">
                            {buildCurlGET(
                                PROVIDERS.fins.baseUrlTest,
                                "/fins/balance",
                                PROVIDERS.fins.headers
                            )}
                        </Code>
                    </section>
                )}

                {/* Проверка статуса */}
                {provider && provider.supportsStatus && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Проверка статуса</h2>

                        {providerKey === "craft" ? (
                            <div>
                                <p className={s.note}>
                                    <code>GET {PROVIDERS.craft.status.path("{transactionId}")}</code>
                                </p>
                                <h4 className={s.h4}>Статусы: <code>PROCESSING</code>, <code>SUCCESS</code>, <code>DECLINED</code></h4>
                                <Code lang="json">{PROVIDERS.craft.status.success}</Code>
                                <h4 className={s.h4}>cURL</h4>
                                <Code lang="bash">{statusCurl}</Code>
                            </div>
                        ) : providerKey === "npay" ? (
                            <div>
                                <p className={s.note}>
                                    Платёж: <code>GET {PROVIDERS.npay.status.path("{transactionId}")}</code>
                                    <br />
                                    Возврат: <code>GET /npay/refunds/{"<transactionId>"}</code>
                                    <br />
                                    Заголовки аутентификации те же.
                                </p>
                                <h4 className={s.h4}>Пример ответа</h4>
                                <Code lang="json">{PROVIDERS.npay.status.success}</Code>
                                <h4 className={s.h4}>Статусы</h4>
                                <Code lang="json">{`// status (наш)  ← providerStatus (NPay)
"SUCCESS"        ← "COMPLETED"
"DECLINED"       ← "CANCELED" / "FAILED" / "EXPIRED"
"PENDING"        ← "ACTIVE" / "NEW"
"PROCESSING"     ← платёж только создан`}</Code>
                            </div>
                        ) : providerKey === "hydra" ? (
                            <div>
                                <p className={s.note}>
                                    Роут{" "}
                                    <code>GET {PROVIDERS.hydra.status.path("{transactionId}")}</code>
                                    <br /> Заголовки аутентификации те же, что при создании.
                                </p>
                                <h4 className={s.h4}>Пример ответа</h4>
                                <Code lang="json">{PROVIDERS.hydra.status.success}</Code>
                                <h4 className={s.h4}>Пример cURL</h4>
                                <Code lang="bash">{statusCurl}</Code>
                            </div>
                        ) : (
                            <div>
                                <p className={s.note}>
                                    Единый роут:
                                    <br />
                                    <code>
                                        GET {PROVIDERS.fins.status.path("{transactionId|payoutId}")}
                                    </code>
                                </p>
                                <h4 className={s.h4}>Пример ответа</h4>
                                <Code lang="json">{PROVIDERS.fins.status.success}</Code>
                                <h4 className={s.h4}>Пример cURL</h4>
                                <Code lang="bash">{statusCurl}</Code>
                            </div>
                        )}
                    </section>
                )}

                {/* Callback */}
                {(providerKey === "craft" ||
                    providerKey === "hydra" ||
                    providerKey === "tkb" ||
                    providerKey === "fins" ||
                    providerKey === "payouts" ||
                    providerKey === "npay") && (
                    <section className={s.card}>
                        <h3 className={s.h3}>
                            Callback (если задан <code>callback_url</code>)
                        </h3>
                        {providerKey === "npay" ? (
                            <div>
                                <p className={s.note}>
                                    Когда NPay присылает вебхук с результатом — мы пересылаем
                                    уведомление на ваш <code>callback_url</code>.
                                </p>
                                <Code lang="json">{PROVIDERS.npay.callbackSample}</Code>
                                <h4 className={s.h4}>Формат вебхука от NPay</h4>
                                <Code lang="json">{PROVIDERS.npay.webhookSample}</Code>
                            </div>
                        ) : (
                            <Code lang="json">
                                {providerKey === "craft"
                                    ? PROVIDERS.craft.callbackSample
                                    : providerKey === "hydra"
                                        ? PROVIDERS.hydra.callbackSample
                                        : providerKey === "tkb"
                                            ? PROVIDERS.tkb.callbackSample
                                            : providerKey === "payouts"
                                                ? PROVIDERS.payouts.callbackSample
                                                : PROVIDERS.fins.callbackSample}
                            </Code>
                        )}
                        <div className={s.note}>
                            POST JSON на ваш колбэк{providerKey === "tkb" ? "." : " с повторными попытками при ошибках сети/5xx."}
                            {providerKey === "tkb" && (
                                <span>
                                    {" "}Доставленным считается только ответ HTTP 200: при любом другом коде, таймауте
                                    или обрыве уведомление повторяется с нарастающими паузами — до 24 попыток примерно за 31 час.
                                </span>
                            )}
                            {providerKey === "payouts" && (
                                <span>
                                    {" "}Ошибки сети, 5xx, 408 и 429 повторяются до трёх суток; остальные 4xx считаются окончательным отказом.
                                </span>
                            )}
                            {providerKey === "payouts" && (
                                <span>
                                    {" "}Подпись в заголовке <Kbd>Sign</Kbd>: HMAC-SHA256 от тела, ключ — ваш merchant key.
                                </span>
                            )}
                        </div>
                    </section>
                )}

                {/* Таблица статусов */}
                {providerKey === "craft" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Статусы платежа (pspState)</h2>
                        <p className={s.note}>
                            Статусы, которые возвращаются в поле <code>pspState</code> при проверке статуса платежа (<code>GET /craft/status/{"{id}"}</code>) или в webhook callback:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Статус</th>
                                    <th>Тип</th>
                                    <th>Описание</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>SUCCESS</code></td>
                                    <td>Финальный</td>
                                    <td>Платёж успешно завершён</td>
                                </tr>
                                <tr>
                                    <td><code>DECLINED</code></td>
                                    <td>Финальный</td>
                                    <td>Платёж отклонён или отменён</td>
                                </tr>
                                <tr>
                                    <td><code>PROCESSING</code></td>
                                    <td>Промежуточный</td>
                                    <td>Платёж в процессе обработки, ожидание подтверждения</td>
                                </tr>
                                <tr>
                                    <td><code>ERROR</code></td>
                                    <td>Финальный</td>
                                    <td>Ошибка при обработке платежа</td>
                                </tr>
                                <tr>
                                    <td><code>ALREADY_EXISTS</code></td>
                                    <td>Ошибка</td>
                                    <td>Платёж с таким ID уже был отправлен (дубликат)</td>
                                </tr>
                                <tr>
                                    <td><code>NOT_FOUND</code></td>
                                    <td>Критическая ошибка</td>
                                    <td>Платёж не найден в системе провайдера</td>
                                </tr>
                                <tr>
                                    <td><code>null</code></td>
                                    <td>Нет данных</td>
                                    <td>Статус недоступен, нет информации от провайдера</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "hydra" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Статусы транзакций</h2>
                        <p className={s.note}>
                            Статусы, которые могут быть возвращены в полях <code>state</code> / <code>hydraState</code>:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Статус</th>
                                    <th>Описание</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>PENDING</code></td>
                                    <td>Платёж создан, ожидает оплаты</td>
                                </tr>
                                <tr>
                                    <td><code>PROCESSING</code></td>
                                    <td>Платёж в обработке</td>
                                </tr>
                                <tr>
                                    <td><code>COMPLETED</code></td>
                                    <td>Платёж успешно завершён</td>
                                </tr>
                                <tr>
                                    <td><code>AUTHORIZED</code></td>
                                    <td>Платёж авторизован (успех)</td>
                                </tr>
                                <tr>
                                    <td><code>DECLINED</code></td>
                                    <td>Платёж отклонён</td>
                                </tr>
                                <tr>
                                    <td><code>CANCELED</code></td>
                                    <td>Платёж отменён пользователем или системой</td>
                                </tr>
                                <tr>
                                    <td><code>EXPIRED</code></td>
                                    <td>Время на оплату истекло</td>
                                </tr>
                                <tr>
                                    <td><code>ERROR</code></td>
                                    <td>Ошибка при обработке платежа</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "tkb" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Статусы транзакций</h2>
                        <p className={s.note}>
                            Статусы, которые могут быть возвращены в поле <code>transactionType</code>:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Статус</th>
                                    <th>Описание</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>PAYOUT_CREATED</code></td>
                                    <td>Выплата создана, ожидает обработки</td>
                                </tr>
                                <tr>
                                    <td><code>PAYOUT_PENDING</code></td>
                                    <td>Выплата в обработке</td>
                                </tr>
                                <tr>
                                    <td><code>PAYOUT</code></td>
                                    <td>Выплата успешно завершена</td>
                                </tr>
                                <tr>
                                    <td><code>PAYOUT_DECLINE</code></td>
                                    <td>Выплата отклонена</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "fins" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Статусы транзакций</h2>
                        <p className={s.note}>
                            Нормализованные статусы в поле <code>status</code> ответа:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Статус</th>
                                    <th>Описание</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>success</code></td>
                                    <td>Выплата успешно завершена</td>
                                </tr>
                                <tr>
                                    <td><code>pending</code></td>
                                    <td>Выплата в обработке</td>
                                </tr>
                                <tr>
                                    <td><code>declined</code></td>
                                    <td>Выплата отклонена</td>
                                </tr>
                            </tbody>
                        </table>
                        <p className={s.note} style={{ marginTop: "1rem" }}>
                            Оригинальные статусы провайдера (поле <code>last_operation_status</code>):
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Статус</th>
                                    <th>Описание</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>approve</code></td>
                                    <td>Операция одобрена (успех)</td>
                                </tr>
                                <tr>
                                    <td><code>approved</code></td>
                                    <td>Операция одобрена (успех)</td>
                                </tr>
                                <tr>
                                    <td><code>pending</code></td>
                                    <td>Операция ожидает обработки</td>
                                </tr>
                                <tr>
                                    <td><code>processing</code></td>
                                    <td>Операция в процессе</td>
                                </tr>
                                <tr>
                                    <td><code>created</code></td>
                                    <td>Операция создана</td>
                                </tr>
                                <tr>
                                    <td><code>decline</code></td>
                                    <td>Операция отклонена</td>
                                </tr>
                                <tr>
                                    <td><code>declined</code></td>
                                    <td>Операция отклонена</td>
                                </tr>
                                <tr>
                                    <td><code>failed</code></td>
                                    <td>Ошибка выполнения</td>
                                </tr>
                                <tr>
                                    <td><code>canceled</code></td>
                                    <td>Операция отменена</td>
                                </tr>
                                <tr>
                                    <td><code>expired</code></td>
                                    <td>Время операции истекло</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "payouts" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Статусы заявки</h2>
                        <p className={s.note}>
                            Статусы, которые возвращаются в полях <code>status</code> / <code>statusCode</code>:
                        </p>
                        <table className={s.table}>
                            <thead>
                                <tr>
                                    <th>Код</th>
                                    <th>Статус</th>
                                    <th>Описание</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>0</code></td>
                                    <td><code>created</code></td>
                                    <td>Заявка создана</td>
                                </tr>
                                <tr>
                                    <td><code>7</code></td>
                                    <td><code>awaitingPayout</code></td>
                                    <td>Ожидает выплаты (антифрод пройден)</td>
                                </tr>
                                <tr>
                                    <td><code>3</code></td>
                                    <td><code>inRegistry</code></td>
                                    <td>Включена в реестр на выплату</td>
                                </tr>
                                <tr>
                                    <td><code>4</code></td>
                                    <td><code>processing</code></td>
                                    <td>Выплата в обработке</td>
                                </tr>
                                <tr>
                                    <td><code>5</code></td>
                                    <td><code>success</code></td>
                                    <td>Выплата выполнена успешно</td>
                                </tr>
                                <tr>
                                    <td><code>6</code></td>
                                    <td><code>failed</code></td>
                                    <td>Выплата не удалась</td>
                                </tr>
                                <tr>
                                    <td><code>1</code></td>
                                    <td><code>antifraudDeclined</code></td>
                                    <td>Заблокировано антифродом</td>
                                </tr>
                                <tr>
                                    <td><code>2</code></td>
                                    <td><code>declined</code></td>
                                    <td>Отклонено</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {providerKey === "payouts" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Аутентификация</h2>
                        <p className={s.note}>
                            Поддерживаются два способа аутентификации:
                        </p>
                        <h4 className={s.h4}>Способ 1: Простая аутентификация</h4>
                        <ul className={s.ul}>
                            <li><code>X-Merchant-UUID</code> — UUID мерчанта</li>
                            <li><code>X-Merchant-Key</code> — API ключ мерчанта</li>
                        </ul>
                        <h4 className={s.h4}>Способ 2: HMAC-подпись</h4>
                        <ul className={s.ul}>
                            <li><code>Merchant</code> — UUID мерчанта</li>
                            <li><code>Sign</code> — HMAC-SHA256 подпись тела запроса вашим ключом</li>
                        </ul>
                        <Code lang="bash">{`# Пример генерации подписи
SIGN=$(echo -n '$JSON_BODY' | openssl dgst -sha256 -hmac '$MERCHANT_KEY' | awk '{print $2}')
curl -X POST 'https://api.e-clear.cc/api/v2/payout-request' \\
  -H 'Content-Type: application/json' \\
  -H 'Merchant: <YOUR-UUID>' \\
  -H 'Sign: '$SIGN \\
  -d '$JSON_BODY'`}</Code>
                    </section>
                )}

                {providerKey === "fins" && (
                    <section className={s.card}>
                        <h2 className={s.h2}>Коды ошибок провайдера (4PMTS)</h2>
                        <p className={s.note}>
                            При отклонении выплаты провайдер возвращает числовой <code>error_code</code> и текстовое описание.
                            Ниже — полный справочник кодов, сгруппированных по категориям.
                            Код ошибки передаётся в поле <code>error.details.error_code</code> ответа.
                        </p>

                        <h3 className={s.h3}>Системные ошибки (100–114)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Код</th><th>Описание</th></tr></thead>
                            <tbody>
                                <tr><td><code>100</code></td><td>Неизвестная системная ошибка</td></tr>
                                <tr><td><code>101</code></td><td>Ресурс не найден (транзакция/выплата не существует)</td></tr>
                                <tr><td><code>102</code></td><td>Операция недоступна для данного заказа</td></tr>
                                <tr><td><code>103</code></td><td>Метод не разрешён для данного заказа</td></tr>
                                <tr><td><code>104</code></td><td>Дублирование запроса (idempotency key)</td></tr>
                                <tr><td><code>105</code></td><td>Ошибка валидации входных параметров</td></tr>
                                <tr><td><code>106</code></td><td>Неверный формат запроса</td></tr>
                                <tr><td><code>107</code></td><td>Превышен лимит запросов (rate limit)</td></tr>
                                <tr><td><code>108</code></td><td>Ошибка авторизации (неверный токен/ключ)</td></tr>
                                <tr><td><code>109</code></td><td>Доступ запрещён (нет прав на операцию)</td></tr>
                                <tr><td><code>110</code></td><td>Операция временно недоступна (обслуживание)</td></tr>
                                <tr><td><code>111</code></td><td>Тайм-аут запроса к провайдеру</td></tr>
                                <tr><td><code>112</code></td><td>Ошибка подключения к провайдеру</td></tr>
                                <tr><td><code>113</code></td><td>Внутренняя ошибка обработки</td></tr>
                                <tr><td><code>114</code></td><td>Неподдерживаемая валюта</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>Антифрод (200–280)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Код</th><th>Описание</th></tr></thead>
                            <tbody>
                                <tr><td><code>200</code></td><td>Транзакция отклонена антифрод-системой</td></tr>
                                <tr><td><code>201</code></td><td>Подозрение на мошенничество</td></tr>
                                <tr><td><code>202</code></td><td>Карта в чёрном списке</td></tr>
                                <tr><td><code>203</code></td><td>IP-адрес в чёрном списке</td></tr>
                                <tr><td><code>204</code></td><td>Страна карты не разрешена</td></tr>
                                <tr><td><code>205</code></td><td>Превышен лимит по сумме</td></tr>
                                <tr><td><code>206</code></td><td>Превышен лимит по количеству операций</td></tr>
                                <tr><td><code>207</code></td><td>Карта просрочена</td></tr>
                                <tr><td><code>210</code></td><td>Отклонено 3D-Secure проверкой</td></tr>
                                <tr><td><code>250</code></td><td>Некорректные данные получателя</td></tr>
                                <tr><td><code>260</code></td><td>Получатель в санкционном списке</td></tr>
                                <tr><td><code>270</code></td><td>Банк получателя не поддерживается</td></tr>
                                <tr><td><code>280</code></td><td>Счёт получателя закрыт или заблокирован</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>3D-Secure (300–308)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Код</th><th>Описание</th></tr></thead>
                            <tbody>
                                <tr><td><code>300</code></td><td>Ошибка 3D-Secure аутентификации</td></tr>
                                <tr><td><code>301</code></td><td>3D-Secure недоступен</td></tr>
                                <tr><td><code>302</code></td><td>Пользователь не прошёл 3D-Secure</td></tr>
                                <tr><td><code>303</code></td><td>Тайм-аут 3D-Secure</td></tr>
                                <tr><td><code>304</code></td><td>Ошибка перенаправления 3D-Secure</td></tr>
                                <tr><td><code>305</code></td><td>Неверный ответ от сервера 3DS</td></tr>
                                <tr><td><code>306</code></td><td>3DS обязателен, но не поддерживается картой</td></tr>
                                <tr><td><code>307</code></td><td>Отклонено издателем при 3DS проверке</td></tr>
                                <tr><td><code>308</code></td><td>3DS верификация не завершена</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>Ошибки провайдера/банка (400–503)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Код</th><th>Описание</th></tr></thead>
                            <tbody>
                                <tr><td><code>400</code></td><td>Общая ошибка провайдера</td></tr>
                                <tr><td><code>401</code></td><td>Недостаточно средств на счёте отправителя</td></tr>
                                <tr><td><code>402</code></td><td>Операция отклонена банком</td></tr>
                                <tr><td><code>403</code></td><td>Превышен лимит по карте</td></tr>
                                <tr><td><code>404</code></td><td>Карта не найдена / неверный номер карты</td></tr>
                                <tr><td><code>405</code></td><td>Карта заблокирована</td></tr>
                                <tr><td><code>406</code></td><td>Карта просрочена</td></tr>
                                <tr><td><code>407</code></td><td>Неверный CVV/CVC</td></tr>
                                <tr><td><code>408</code></td><td>Тип карты не поддерживается</td></tr>
                                <tr><td><code>409</code></td><td>Банк-эмитент недоступен</td></tr>
                                <tr><td><code>410</code></td><td>Повторная транзакция (дубликат)</td></tr>
                                <tr><td><code>411</code></td><td>Неверная сумма транзакции</td></tr>
                                <tr><td><code>412</code></td><td>Неверная валюта</td></tr>
                                <tr><td><code>420</code></td><td>Техническая ошибка провайдера</td></tr>
                                <tr><td><code>430</code></td><td>Тайм-аут ответа от банка-эквайера</td></tr>
                                <tr><td><code>440</code></td><td>Операция не поддерживается терминалом</td></tr>
                                <tr><td><code>450</code></td><td>Недостаточно средств для выплаты</td></tr>
                                <tr><td><code>500</code></td><td>Внутренняя ошибка платёжного сервиса</td></tr>
                                <tr><td><code>501</code></td><td>Сервис временно недоступен</td></tr>
                                <tr><td><code>502</code></td><td>Ошибка шлюза</td></tr>
                                <tr><td><code>503</code></td><td>Сервис перегружен</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>Ответы банка-эмитента (601–699)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Код</th><th>Описание</th></tr></thead>
                            <tbody>
                                <tr><td><code>601</code></td><td>Отказ эмитента без объяснения причин</td></tr>
                                <tr><td><code>602</code></td><td>Подозрение на мошенничество (эмитент)</td></tr>
                                <tr><td><code>603</code></td><td>Карта ограничена для данного типа операций</td></tr>
                                <tr><td><code>604</code></td><td>Карта изъята / потеряна / украдена</td></tr>
                                <tr><td><code>605</code></td><td>Неверный PIN</td></tr>
                                <tr><td><code>606</code></td><td>Превышено количество попыток ввода PIN</td></tr>
                                <tr><td><code>607</code></td><td>Держателю карты следует обратиться к эмитенту</td></tr>
                                <tr><td><code>610</code></td><td>Сумма превышает допустимый лимит</td></tr>
                                <tr><td><code>620</code></td><td>Недостаточно средств</td></tr>
                                <tr><td><code>630</code></td><td>Счёт закрыт</td></tr>
                                <tr><td><code>640</code></td><td>Операция отклонена: ограничения по стране</td></tr>
                                <tr><td><code>650</code></td><td>Неверные реквизиты карты</td></tr>
                                <tr><td><code>660</code></td><td>Тайм-аут ответа эмитента</td></tr>
                                <tr><td><code>699</code></td><td>Прочий отказ эмитента</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>Расширенные коды (720–779)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Код</th><th>Описание</th></tr></thead>
                            <tbody>
                                <tr><td><code>720</code></td><td>Ошибка маршрутизации платежа</td></tr>
                                <tr><td><code>721</code></td><td>Неверные параметры рекуррентного платежа</td></tr>
                                <tr><td><code>722</code></td><td>Подписка отменена</td></tr>
                                <tr><td><code>723</code></td><td>Токен карты недействителен</td></tr>
                                <tr><td><code>730</code></td><td>Возврат невозможен (истёк срок)</td></tr>
                                <tr><td><code>731</code></td><td>Частичный возврат не поддерживается</td></tr>
                                <tr><td><code>740</code></td><td>Операция требует подтверждения пользователя</td></tr>
                                <tr><td><code>750</code></td><td>Неверный формат номера телефона</td></tr>
                                <tr><td><code>760</code></td><td>Неверный формат email</td></tr>
                                <tr><td><code>770</code></td><td>Неверные персональные данные получателя</td></tr>
                                <tr><td><code>779</code></td><td>Прочие ошибки валидации</td></tr>
                            </tbody>
                        </table>

                        <h3 className={s.h3}>СБП-коды (801–854)</h3>
                        <table className={s.table}>
                            <thead><tr><th>Код</th><th>Описание</th></tr></thead>
                            <tbody>
                                <tr><td><code>801</code></td><td>Банк получателя не является участником СБП</td></tr>
                                <tr><td><code>802</code></td><td>Получатель не найден в системе СБП</td></tr>
                                <tr><td><code>803</code></td><td>Счёт получателя не привязан к СБП</td></tr>
                                <tr><td><code>804</code></td><td>Тайм-аут СБП-перевода</td></tr>
                                <tr><td><code>805</code></td><td>СБП-перевод отклонён банком получателя</td></tr>
                                <tr><td><code>806</code></td><td>Неверный формат номера телефона для СБП</td></tr>
                                <tr><td><code>807</code></td><td>Неверный bank_member_id</td></tr>
                                <tr><td><code>808</code></td><td>Превышен лимит СБП-переводов</td></tr>
                                <tr><td><code>810</code></td><td>Технический сбой СБП</td></tr>
                                <tr><td><code>820</code></td><td>СБП недоступна (регламентные работы)</td></tr>
                                <tr><td><code>830</code></td><td>Получатель ограничил приём СБП-переводов</td></tr>
                                <tr><td><code>840</code></td><td>Неверные параметры QR-кода СБП</td></tr>
                                <tr><td><code>850</code></td><td>Отклонено антифрод-системой СБП</td></tr>
                                <tr><td><code>851</code></td><td>Подозрительная операция (СБП)</td></tr>
                                <tr><td><code>852</code></td><td>Получатель в стоп-листе СБП</td></tr>
                                <tr><td><code>853</code></td><td>Сумма ниже минимальной для СБП</td></tr>
                                <tr><td><code>854</code></td><td>Сумма превышает максимальную для СБП</td></tr>
                            </tbody>
                        </table>
                    </section>
                )}

                <footer className={s.footer}>
                    <span>&copy; E-Clear</span>
                    <span className={s.dot}>&bull;</span>
                    <a className={s.link} href="#top">
                        Наверх
                    </a>
                </footer>
            </main>
        </div>
    );
};

export default MerchantsDocs;



// WEBPACK FOOTER //
// ./src/PagesOfCommon/wiki/index.js